import React, { useEffect, useMemo, useState } from 'react';
import { Chart, Line } from 'react-chartjs-2';
import {
  fetchInvestingJson,
  fundamentalsRowsFromStockScreen,
} from '../utils/investingApi';
import { ensureFundamentalsChartsRegistered } from '../utils/fundamentalsChartSetup';
import './StockFundamentalsDashboard.css';

ensureFundamentalsChartsRegistered();

const SERIES = [
  { id: 'gross_margin', label: 'Gross margin', group: 'profit', fmt: 'pct' },
  { id: 'operating_margin', label: 'Operating margin', group: 'profit', fmt: 'pct' },
  { id: 'net_margin', label: 'Net margin', group: 'profit', fmt: 'pct' },
  { id: 'roa', label: 'ROA', group: 'profit', fmt: 'pct' },
  { id: 'roe', label: 'ROE', group: 'profit', fmt: 'pct' },
  { id: 'pe', label: 'P/E', group: 'value', fmt: 'mult' },
  { id: 'pb', label: 'P/B', group: 'value', fmt: 'mult' },
  { id: 'p_cf', label: 'P/CF', group: 'value', fmt: 'mult' },
  { id: 'p_fcf', label: 'P/FCF', group: 'value', fmt: 'mult' },
  { id: 'ev_to_sales', label: 'EV/Sales', group: 'value', fmt: 'mult' },
];

const DEFAULT_ON = new Set(['operating_margin', 'roe', 'pe']);

const COLORS = [
  '#173CA1',
  '#1F4E8C',
  '#C0571F',
  '#7A3187',
  '#A16917',
  '#1763A1',
  '#387F3C',
  '#A11745',
  '#4A5672',
  '#4A5672',
];

function fmtVal(fmt, v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (fmt === 'pct') return `${(n * 100).toFixed(1)}%`;
  if (fmt === 'mult') return n.toFixed(2);
  return String(n);
}

async function loadFundamentalsRows(symbol) {
  const enc = encodeURIComponent(symbol);
  try {
    const { res, data } = await fetchInvestingJson(
      `/api/investing/stocks/fundamentals/${enc}`,
    );
    if (res.ok && Array.isArray(data.rows) && data.rows.length > 0) {
      return { rows: data.rows, source: 'cache' };
    }
  } catch {
    /* fall through to live screen */
  }

  const { res, data } = await fetchInvestingJson(
    `/api/investing/stock-screen/${enc}?years=10&valuation=1`,
  );
  if (!res.ok) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  const rows = fundamentalsRowsFromStockScreen(data);
  if (!rows.length) {
    throw new Error('No SEC profitability data for this symbol');
  }
  return { rows, source: 'live' };
}

function StockFundamentalsDashboard({ symbol }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState(null);
  const [active, setActive] = useState(() => new Set(DEFAULT_ON));

  useEffect(() => {
    if (!symbol) {
      setRows([]);
      setError(null);
      setSource(null);
      return undefined;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await loadFundamentalsRows(symbol);
        if (cancelled) return;
        setRows(result.rows);
        setSource(result.source);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load fundamentals');
          setRows([]);
          setSource(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  const labels = useMemo(
    () => rows.map((r) => String(r.fiscal_year)),
    [rows],
  );

  const chartData = useMemo(() => {
    const datasets = [];
    let colorIdx = 0;
    SERIES.forEach((s) => {
      if (!active.has(s.id)) return;
      const color = COLORS[colorIdx % COLORS.length];
      colorIdx += 1;
      datasets.push({
        label: s.label,
        data: rows.map((r) => (r[s.id] != null ? Number(r[s.id]) : null)),
        borderColor: color,
        backgroundColor: color,
        tension: 0.2,
        spanGaps: false,
        pointRadius: 3,
        yAxisID: s.fmt === 'pct' ? 'yPct' : 'yMult',
      });
    });
    return { labels, datasets };
  }, [rows, labels, active]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: { color: '#16244A', boxWidth: 12, font: { size: 10 } },
        },
        tooltip: {
          callbacks: {
            label(ctx) {
              const s = SERIES.find((x) => x.label === ctx.dataset.label);
              return `${ctx.dataset.label}: ${fmtVal(s?.fmt, ctx.parsed.y)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: '#E3D9C6' },
          ticks: { color: '#4A5672', maxRotation: 0 },
        },
        yPct: {
          type: 'linear',
          position: 'left',
          grid: { color: '#E3D9C6' },
          ticks: {
            color: '#4A5672',
            callback: (v) => `${(v * 100).toFixed(0)}%`,
          },
        },
        yMult: {
          type: 'linear',
          position: 'right',
          grid: { drawOnChartArea: false },
          ticks: { color: '#4A5672' },
        },
      },
    }),
    [],
  );

  function toggleSeries(id) {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (!symbol) return null;

  return (
    <section className="stock-fundamentals-dash" aria-label="Profitability and valuation">
      <header className="stock-fundamentals-head">
        <h3 className="stock-fundamentals-title">Profitability and valuation</h3>
        <p className="stock-fundamentals-sub">
          SEC annual metrics by fiscal year. Valuation multiples use latest FY when live;
          cached rows use year-end prices. Gaps mean incomplete filings or missing inputs.
          {source === 'live' && ' (live SEC screen — run fundamentals import to cache.)'}
        </p>
      </header>

      <div className="stock-fundamentals-series">
        <span className="stock-fundamentals-series-label">Series</span>
        {SERIES.map((s) => (
          <label key={s.id} className="stock-fundamentals-check">
            <input
              type="checkbox"
              checked={active.has(s.id)}
              onChange={() => toggleSeries(s.id)}
            />
            {s.label}
          </label>
        ))}
      </div>

      {loading && <p className="stock-fundamentals-msg">Loading fundamentals…</p>}
      {error && !loading && (
        <p className="stock-fundamentals-error">{error}</p>
      )}
      {!loading && !error && rows.length > 0 && (
        <div className="stock-fundamentals-chart-wrap">
          <Chart type="line" data={chartData} options={chartOptions} />
        </div>
      )}
    </section>
  );
}

export default StockFundamentalsDashboard;
